import { Stack } from '@mui/material';
import { Genders } from '@schooly/constants';
import { FC, PropsWithChildren, useCallback, useState } from 'react';
import { $enum } from 'ts-enum-util';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { toggleMultipleValueArrayProperty } from '../utils';
import { GenderTagSelect, GenderTagSelectProps } from './GenderTagSelect';

type GenderExpandedSelectProps = PropsWithChildren<{
  selectedGenders: Genders[];
  onApply: (v: Genders[]) => void;
  onClear: () => void;
  onClose: () => void;
  applyImmediate?: boolean;
}>;

const OPTIONS = $enum(Genders).map((v) => v);

export const GenderExpandedSelect: FC<GenderExpandedSelectProps> = ({
  selectedGenders: initialSelectedGenders,
  onApply,
  onClose,
  onClear,
  applyImmediate = false,
}) => {
  const [selectedGenders, setSelectedGenders] = useState<Genders[]>(initialSelectedGenders);

  const toggleGenders = useCallback(
    (v: Genders[]) => {
      setSelectedGenders((genders) => {
        const newGenders = toggleMultipleValueArrayProperty(genders, v);
        if (applyImmediate) {
          onApply(newGenders);
        }
        return newGenders;
      });
    },
    [applyImmediate, onApply],
  );

  const handleApply = useCallback(() => {
    onApply(selectedGenders);
    onClose();
  }, [selectedGenders, onApply, onClose]);

  const renderContent = useCallback(() => {
    return (
      <Stack m={1} gap={2}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {renderGenderTags({
            genders: OPTIONS,
            tagProps: (status) => ({
              variant: selectedGenders.includes(status) ? 'filled' : undefined,
            }),
            onClick: (gender) => toggleGenders([gender]),
          })}
        </Stack>
        {!applyImmediate && <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />}
      </Stack>
    );
  }, [handleApply, onClear, toggleGenders, selectedGenders, applyImmediate]);

  return (
    <ExpandedSelect
      hasSelectedValue={selectedGenders.length > 0}
      onClose={onClose}
      onClear={onClear}
      renderContent={renderContent}
    >
      {renderGenderTags({
        tagProps: { size: 'small' },
        genders: selectedGenders,
        onDelete: (gender) => toggleGenders([gender]),
      })}
    </ExpandedSelect>
  );
};

type RenderGenderTagsParams = {
  genders: Genders[];
  onDelete?: (v: Genders) => void;
  onClick?: (v: Genders) => void;
  tagProps?:
    | Omit<GenderTagSelectProps, 'gender'>
    | ((v: Genders) => Omit<GenderTagSelectProps, 'gender'>);
};

export const renderGenderTags = ({
  genders,
  onDelete,
  onClick,
  tagProps,
}: RenderGenderTagsParams) =>
  genders.map((v) => (
    <GenderTagSelect
      key={v}
      gender={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
